import { useEffect, useState } from "react";
import { Loader2, Sparkles, Download, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SimpleSelect } from "@/components/FormField";
import { Markdown } from "@/components/ModulePage";
import { api, errMsg } from "@/lib/api";

const MODES = [["locate", "Find where it lives"], ["explain", "Explain this code"], ["suggest", "Suggest a change"], ["link", "Link / unlink sections"]];
const MODELS = [["claude-sonnet-5-5", "Claude Sonnet 5.5"], ["claude-opus-5-5", "Claude Opus 5.5"], ["gpt-6-luna", "GPT 6 Luna"]];
const HINTS = { locate: "e.g. Where is the readiness score calculated and shown?", explain: "e.g. How does the document approval workflow work?",
  suggest: "e.g. Add a 'Due this week' filter to the tracker", link: "e.g. Show unpaid plan status on the dashboard" };

const saveMd = (text) => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([text], { type: "text/markdown" })); a.download = "isocred-ai-answer.md"; a.click(); };

export function DevAI({ sections, ctx, setCtx }) {
  const [q, setQ] = useState("");
  const [model, setModel] = useState("claude-sonnet-5-5");
  const [busy, setBusy] = useState(false);
  const [ans, setAns] = useState("");
  const [hist, setHist] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const loadHist = () => api.get("/dev/ai/history").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); }, []);
  const opts = sections.map((s) => [s.id, s.label]);
  const run = async () => {
    setBusy(true); setAns("");
    try {
      const { data } = await api.post("/dev/ai", { mode: ctx.mode, question: q, section_id: ctx.section_id || null, target_section_id: ctx.mode === "link" ? ctx.target || null : null, path: ctx.path || null, model });
      const t0 = Date.now();
      while (Date.now() - t0 < 6 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 1200));
        const j = (await api.get(`/dev/ai/jobs/${data.job_id}`)).data;
        if (j.text) setAns(j.text);
        if (j.status === "done") { loadHist(); return; }
        if (j.status === "failed") throw new Error(j.error);
      }
      throw new Error("AI request timed out. Please retry");
    } catch (e) { toast.error(e.response ? errMsg(e) : e.message); } finally { setBusy(false); }
  };
  return (
    <div className="qt-card p-4 flex flex-col gap-3" data-testid="dev-ai-panel">
      <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#48217A]" /><h3 className="font-display font-semibold">AI code assistant</h3>
        <button className="ml-auto text-xs text-[#48217A] inline-flex items-center gap-1" onClick={() => setShowHist(!showHist)} data-testid="dev-ai-history-toggle"><History className="h-3.5 w-3.5" />History</button></div>
      {showHist ? (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="dev-ai-history">{hist.length === 0 && <div className="text-xs text-[#5A6B87]">No answers yet.</div>}
          {hist.map((h) => <button key={h.id} onClick={() => { setAns(h.text); setShowHist(false); }} className="block w-full text-left border rounded p-2 text-xs hover:bg-slate-50"><b className="capitalize">{h.mode}</b> · {h.question.slice(0, 90)}</button>)}</div>
      ) : (<>
        <SimpleSelect value={ctx.mode} onChange={(v) => setCtx({ ...ctx, mode: v })} options={MODES} testid="dev-ai-mode" />
        <SimpleSelect value={ctx.section_id} onChange={(v) => setCtx({ ...ctx, section_id: v })} options={opts} placeholder="Any section (AI finds it)" allowNone testid="dev-ai-section" />
        {ctx.mode === "link" && <SimpleSelect value={ctx.target} onChange={(v) => setCtx({ ...ctx, target: v })} options={opts} placeholder="Link with section…" testid="dev-ai-target" />}
        {ctx.path && <div className="text-[11px] font-mono bg-[#F3EFFA] text-[#48217A] rounded px-2 py-1 flex justify-between gap-2" data-testid="dev-ai-file-ctx"><span className="truncate">{ctx.path}</span><button onClick={() => setCtx({ ...ctx, path: null })}>×</button></div>}
        <Textarea rows={4} value={q} onChange={(e) => setQ(e.target.value)} placeholder={HINTS[ctx.mode]} data-testid="dev-ai-question" />
        <div className="flex gap-2"><div className="flex-1"><SimpleSelect value={model} onChange={setModel} options={MODELS} testid="dev-ai-model" /></div>
          <Button disabled={busy || q.trim().length < 3} onClick={run} className="bg-[#48217A]" data-testid="dev-ai-run">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}</Button></div>
      </>)}
      {ans && !showHist && (
        <div className="border-t pt-3"><div className="flex justify-end mb-1"><button onClick={() => saveMd(ans)} className="text-xs text-[#48217A] inline-flex items-center gap-1" data-testid="dev-ai-save"><Download className="h-3.5 w-3.5" />Save as .md</button></div>
          <div className="max-h-[55vh] overflow-y-auto text-[13px]" data-testid="dev-ai-answer"><Markdown>{ans}</Markdown></div></div>
      )}
      <p className="text-[11px] text-[#5A6B87]">AI suggestions are proposals. Apply changes in the Emergent editor, then publish to make them live.</p>
    </div>
  );
}
